// src/screens/admin/VuelosRespuestasListas.jsx
// Sub-sección "Respuestas" del panel Vuelos Guyana: mensajes de WhatsApp ya escritos para el paquete
// "hasta Boa Vista" / "paquete completo", uno por ruta, con el precio puesto solo. Se elige primero
// cómo paga el cliente (Zelle o USD en efectivo en Cuba) y solo salen las rutas que aceptan ese pago.
// El precio del pasaje es el MÁS BAJO de la ruta (el paquete sirve para cualquier fecha), en Zelle o en USD
// Cuba con el mismo cálculo que ve el cliente en la app (% de la ruta o del proveedor, y su redondeo).
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import { getRutasAdmin, calcPrecioUsdCuba } from '../../services/vuelosGuyanaService';

// Valores fijos del paquete (USD). Si cambian, se cambian aquí.
const RESERVA = 50; // se paga con el pasaje para agendar el viaje Guyana -> Boa Vista
const AL_LLEGAR_BOA_VISTA = 350; // opción "hasta Boa Vista"
const AL_LLEGAR_COMPLETO = 950; // opción paquete completo
const HOSTAL_DIA = 20; // hostal en Boa Vista, opción "hasta Boa Vista"

const ORDEN_CIUDADES = ['Habana', 'Camagüey', 'Santiago', 'Holguín'];
const NOMBRE_CIUDAD = { Habana: 'La Habana', Santiago: 'Santiago de Cuba' };

// Pasaje más barato de la ruta en Zelle, sin mirar la fecha.
const pasajeZelleMinimo = (ruta) => {
  if (ruta.escalonado) {
    const niveles = [ruta.precio_zelle_barato, ruta.precio_zelle_medio, ruta.precio_zelle_caro]
      .filter((v) => v != null).map((v) => parseFloat(v));
    if (niveles.length) return Math.round(Math.min(...niveles));
  }
  return Math.round(parseFloat(ruta.precio_zelle));
};

const aceptaZelle = (ruta) => !ruta.solo_usd_cuba;
const aceptaUsd = (ruta) => !!ruta.admite_usd_cuba || !!ruta.solo_usd_cuba;

// Pasaje (sin los 50 de reserva) según cómo paga el cliente.
const pasajeSegunPago = (ruta, pago) => {
  const zelle = pasajeZelleMinimo(ruta);
  if (pago === 'zelle') return zelle;
  const pct = ruta.porcentaje_usd_cuba_ruta ?? ruta.proveedor_porcentaje_usd_cuba ?? 0;
  return calcPrecioUsdCuba(zelle, pct, ruta.porcentaje_usd_cuba_ruta != null);
};

const mensajeHastaBoaVista = (ciudad, pasaje, pago) => {
  const cuanto = pago === 'zelle' ? `${pasaje} vía Zelle` : `${pasaje} en efectivo`;
  return (
`Las personas que compran el paquete no tienen que limitarse a una fecha específica del plan de vuelos.
Por ejemplo, si quisiera volar en la fecha más próxima que tenemos disponible por ciudad y aerolínea, lo podría hacer.

Hasta Boa Vista saliendo de ${ciudad} pagas el pasaje ${cuanto} más ${RESERVA} USD para agendar el viaje desde Guyana hasta Boa Vista. El resto se paga al llegar a Boa Vista: ${AL_LLEGAR_BOA_VISTA} USD.

Esta opción no incluye hostal en Boa Vista. Es la mejor si piensas hacer documentación allá. Y si necesitas hostal, son ${HOSTAL_DIA} USD por día.`);
};

const mensajeCompleto = () =>
`La otra opción es viajar completo hasta la ciudad de destino en Brasil, sin hacer documentos.

Aquí sí te damos hostal todos los días que hagan falta hasta entregarte el itinerario completo, con desayuno, almuerzo y comida garantizado.

En vez de los ${AL_LLEGAR_BOA_VISTA} pagas ${AL_LLEGAR_COMPLETO} USD al llegar a Boa Vista. El total lo tienes en la tabla.

Funciona así: te recogemos en el aeropuerto de Guyana y te llevamos a un hostal. Normalmente ese mismo día sales hacia Boa Vista.

Allá vas a otro hostal con las tres comidas que te mencioné. Ya tendrás en la mano todos los boletos que faltan hasta tu destino final, así que literalmente solo te tocará subirte a cada medio de transporte.

Si te queda alguna duda antes de decidir, podemos marcar una llamada y te aclaro lo que sea necesario.`;

// El efectivo de InterCaribbean y de SKY HIGH saliendo por La Habana solo se paga en La Habana.
// Aruba por La Habana, y SKY HIGH por Santiago o Holguín, se pagan en efectivo en cualquiera de las
// provincias donde se recibe: La Habana, Matanzas, Camagüey, Holguín, Granma y Santiago de Cuba.
const efectivoSoloEnHabana = (ruta) =>
  ruta.ciudad_salida === 'Habana' && /intercaribbean|sky\s*high/i.test(ruta.aerolinea || '');

// "Escala en Santo Domingo y Bogotá" -> 2 escalas; sin escala -> vuelo directo.
const describirEscalas = (ruta) => {
  const txt = (ruta.escala || '').replace(/^escalas?\s+en\s+/i, '').trim();
  if (!txt) return 'vuelo directo';
  const n = txt.split(/\s+y\s+|,|\//).filter(Boolean).length;
  return n === 1 ? '1 escala' : `${n} escalas`;
};

// Salidas por OTRA ciudad que cuestan menos que la salida más barata de esta ciudad.
const alternativasMasBaratas = (ciudad, visibles, pago) => {
  const propias = visibles.filter((r) => r.ciudad_salida === ciudad);
  if (!propias.length) return [];
  const base = Math.min(...propias.map((r) => pasajeSegunPago(r, pago)));
  return visibles
    .filter((r) => r.ciudad_salida !== ciudad && pasajeSegunPago(r, pago) < base)
    .map((r) => ({ ruta: r, ahorro: base - pasajeSegunPago(r, pago) }))
    .sort((a, b) => b.ahorro - a.ahorro);
};

const mensajeAhorro = (ciudad, elegidas, pago) => {
  const nombre = (c) => NOMBRE_CIUDAD[c] || c;
  const lineas = elegidas.map(({ ruta, ahorro }) => {
    const nota = pago === 'usd' && efectivoSoloEnHabana(ruta) && ciudad !== 'Habana'
      ? ' El pago en efectivo es solo en La Habana.' : '';
    return `• ${nombre(ruta.ciudad_salida)} con ${ruta.aerolinea} (${describirEscalas(ruta)}): ${ahorro} USD más económico por persona.${nota}`;
  });
  const cierre = pago === 'usd' && elegidas.some((e) => !efectivoSoloEnHabana(e.ruta))
    ? `\n\nLas demás salidas las puedes pagar en efectivo en ${nombre(ciudad)} mismo (también se recibe en La Habana, Matanzas, Camagüey, Holguín, Granma y Santiago de Cuba).` : '';
  return `Si te sirve salir por otra ciudad, el pasaje sale más económico que saliendo por ${nombre(ciudad)}:\n\n${lineas.join('\n')}${cierre}\n\nEl resto del paquete es exactamente igual.`;
};

const copiar = async (texto, aviso) => {
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    // Respaldo para navegadores que bloquean el portapapeles
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch { toast('No se pudo copiar.', 'error'); document.body.removeChild(ta); return; }
    document.body.removeChild(ta);
  }
  toast(aviso);
};

const btn = (activo) => ({
  flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 700,
  background: activo ? 'var(--orange)' : 'transparent', color: activo ? 'white' : 'var(--muted)',
});

export default function VuelosRespuestasListas() {
  const [rutas, setRutas] = useState(null);
  const [pago, setPago] = useState(() => {
    try { return localStorage.getItem('respuestas_pago') === 'usd' ? 'usd' : 'zelle'; } catch { return 'zelle'; }
  });

  // Rutas que NO quieres ofrecer como "salir por otra ciudad" (se recuerdan en este navegador)
  const [excluidas, setExcluidas] = useState(() => {
    try { return JSON.parse(localStorage.getItem('respuestas_excluidas') || '[]'); } catch { return []; }
  });
  const alternarExcluida = (id) => {
    const nuevas = excluidas.includes(id) ? excluidas.filter((x) => x !== id) : [...excluidas, id];
    setExcluidas(nuevas);
    try { localStorage.setItem('respuestas_excluidas', JSON.stringify(nuevas)); } catch { /* sin almacenamiento */ }
  };

  useEffect(() => { getRutasAdmin().then(setRutas); }, []);

  const elegirPago = (p) => {
    setPago(p);
    try { localStorage.setItem('respuestas_pago', p); } catch { /* sin almacenamiento */ }
  };

  if (!rutas) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando...</p>;

  const visibles = rutas.filter((r) =>
    r.activa && (r.destino || '').toLowerCase() === 'guyana' && (pago === 'zelle' ? aceptaZelle(r) : aceptaUsd(r)));
  const ciudades = [...new Set(visibles.map((r) => r.ciudad_salida))]
    .sort((a, b) => (ORDEN_CIUDADES.indexOf(a) + 1 || 99) - (ORDEN_CIUDADES.indexOf(b) + 1 || 99));

  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
        Primero pregunta al cliente cómo va a pagar. Toca un botón y el mensaje queda copiado, con el precio ya puesto.
      </p>
      <div style={{ display: 'flex', background: 'var(--navy-card)', borderRadius: 10, padding: 4, marginBottom: 16, gap: 4 }}>
        <button onClick={() => elegirPago('zelle')} style={btn(pago === 'zelle')}>💳 Paga por Zelle</button>
        <button onClick={() => elegirPago('usd')} style={btn(pago === 'usd')}>💵 USD en efectivo en Cuba</button>
      </div>

      {ciudades.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13 }}>No hay rutas activas a Guyana para este tipo de pago.</p>}

      {ciudades.map((ciudad) => (
        <div key={ciudad} style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: 'var(--orange)', marginBottom: 8 }}>
            {(NOMBRE_CIUDAD[ciudad] || ciudad).toUpperCase()}
          </p>
          {visibles.filter((r) => r.ciudad_salida === ciudad).map((r) => {
            const pasaje = pasajeSegunPago(r, pago);
            const ahora = pasaje + RESERVA;
            const nombreCiudad = NOMBRE_CIUDAD[ciudad] || ciudad;
            return (
              <div key={r.id} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
                <p style={{ fontSize: 14, fontWeight: 700 }}>
                  #{r.numero} · {r.aerolinea || 'Sin aerolínea'}
                  {r.escala ? <span style={{ fontWeight: 400, color: 'var(--muted)' }}> · {r.escala.trim()}</span> : null}
                </p>
                <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 10px' }}>
                  Pasaje {pasaje} · Pagas ahora <b style={{ color: 'var(--gold)' }}>{ahora}</b> ·
                  Hasta Boa Vista <b style={{ color: 'var(--gold)' }}>{ahora + AL_LLEGAR_BOA_VISTA}</b> ·
                  Completo <b style={{ color: 'var(--gold)' }}>{ahora + AL_LLEGAR_COMPLETO}</b>
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => copiar(mensajeHastaBoaVista(nombreCiudad, pasaje, pago), 'Mensaje "Hasta Boa Vista" copiado ✅')}
                    style={{ flex: 1, background: 'var(--navy)', border: '1px solid var(--orange)', color: 'var(--orange)', borderRadius: 8, padding: '9px 6px', fontSize: 12, fontWeight: 700 }}>
                    📋 Hasta Boa Vista
                  </button>
                  <button onClick={() => copiar(mensajeCompleto(), 'Mensaje "Paquete completo" copiado ✅')}
                    style={{ flex: 1, background: 'var(--navy)', border: '1px solid var(--orange)', color: 'var(--orange)', borderRadius: 8, padding: '9px 6px', fontSize: 12, fontWeight: 700 }}>
                    📋 Paquete completo
                  </button>
                </div>
              </div>
            );
          })}
          {(() => {
            const alts = alternativasMasBaratas(ciudad, visibles, pago);
            if (!alts.length) return null;
            const elegidas = alts.filter((a) => !excluidas.includes(a.ruta.id));
            return (
              <details style={{ background: 'var(--navy-card)', border: '1px dashed var(--navy-border)', borderRadius: 12, padding: 12 }}>
                <summary style={{ fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  💡 Salir por otra ciudad sale más barato ({alts.length})
                </summary>
                <div style={{ marginTop: 10 }}>
                  {alts.map(({ ruta, ahorro }) => (
                    <label key={ruta.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                      <input type="checkbox" checked={!excluidas.includes(ruta.id)} onChange={() => alternarExcluida(ruta.id)} />
                      <span>{(NOMBRE_CIUDAD[ruta.ciudad_salida] || ruta.ciudad_salida)} · {ruta.aerolinea} ({describirEscalas(ruta)}) · <b style={{ color: 'var(--gold)' }}>-{ahorro} USD</b></span>
                    </label>
                  ))}
                  <button disabled={!elegidas.length}
                    onClick={() => copiar(mensajeAhorro(ciudad, elegidas, pago), 'Mensaje de ahorro copiado ✅')}
                    style={{ width: '100%', marginTop: 6, background: 'var(--navy)', border: '1px solid var(--orange)', color: 'var(--orange)', borderRadius: 8, padding: '9px 6px', fontSize: 12, fontWeight: 700, opacity: elegidas.length ? 1 : 0.4 }}>
                    📋 Copiar mensaje de ahorro
                  </button>
                </div>
              </details>
            );
          })()}
        </div>
      ))}
    </div>
  );
}
